import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { sucursales as apiSucursales, usuarios as apiUsuarios } from '../api/servicios'
import { fechaHora } from '../api/formato'
import type { Rol, Sucursal, UsuarioAdmin } from '../api/tipos'
import { Dialogo } from '../componentes/Dialogo'
import { Estado } from '../componentes/Estado'
import { usarRetardo } from '../componentes/Paginador'
import { mensajeDeError, usarCargar } from '../componentes/usarCargar'
import './listados.css'

type Ventana =
    | { tipo: 'ninguno' }
    | { tipo: 'nuevo' }
    | { tipo: 'editar'; usuario: UsuarioAdmin }
    | { tipo: 'contrasena'; usuario: UsuarioAdmin }

export function Usuarios() {
    const [texto, setTexto] = useState('')
    const [ventana, setVentana] = useState<Ventana>({ tipo: 'ninguno' })

    const busqueda = usarRetardo(texto)

    const roles = usarCargar(() => apiUsuarios.roles(), [])
    const lista = usarCargar(() => apiUsuarios.listar(busqueda || undefined), [busqueda])

    function refrescar() {
        setVentana({ tipo: 'ninguno' })
        lista.recargar()
    }

    return (
        <div className="pagina-listado columna g4">
            <header className="fila entre envuelve g3">
                <h1>Usuarios</h1>

                <button
                    type="button"
                    className="boton boton-primario"
                    onClick={() => setVentana({ tipo: 'nuevo' })}
                >
                    Nuevo usuario
                </button>
            </header>

            <section className="panel columna g3">
                <input
                    className="campo"
                    type="search"
                    placeholder="Buscar por nombre o usuario"
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                />

                <Estado
                    cargando={lista.cargando}
                    error={lista.error}
                    alReintentar={lista.recargar}
                    vacio={(lista.datos?.items ?? []).length === 0}
                    mensajeVacio="Ningún usuario coincide con la búsqueda."
                >
                    <div className="tabla-envoltura">
                        <table className="tabla">
                            <thead>
                                <tr>
                                    <th>Usuario</th>
                                    <th className="oculto-angosto">Roles</th>
                                    <th className="oculto-angosto">Último acceso</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {(lista.datos?.items ?? []).map((u) => (
                                    <tr key={u.usuarioId}>
                                        <td>
                                            <div className="columna">
                                                <span>{u.nombreCompleto}</span>
                                                <span className="tenue diminuto numero">
                                                    {u.nombreUsuario}
                                                    {u.sucursalNombre && ` · ${u.sucursalNombre}`}
                                                    {!u.activo && ' · inactivo'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="oculto-angosto chico">
                                            {u.roles.join(', ') || '—'}
                                        </td>
                                        <td className="oculto-angosto tenue chico">
                                            {fechaHora(u.ultimoAccesoEn)}
                                        </td>
                                        <td className="derecha">
                                            <div className="fila g2 acciones-fila">
                                                <button
                                                    type="button"
                                                    className="boton boton-fantasma"
                                                    onClick={() =>
                                                        setVentana({ tipo: 'contrasena', usuario: u })
                                                    }
                                                >
                                                    Contraseña
                                                </button>
                                                <button
                                                    type="button"
                                                    className="boton boton-fantasma"
                                                    onClick={() =>
                                                        setVentana({ tipo: 'editar', usuario: u })
                                                    }
                                                >
                                                    Editar
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Estado>
            </section>

            {(ventana.tipo === 'nuevo' || ventana.tipo === 'editar') && (
                <DialogoUsuario
                    usuario={ventana.tipo === 'editar' ? ventana.usuario : null}
                    roles={roles.datos ?? []}
                    cargandoRoles={roles.cargando}
                    errorRoles={roles.error}
                    alReintentarRoles={roles.recargar}
                    alGuardar={refrescar}
                    alCerrar={() => setVentana({ tipo: 'ninguno' })}
                />
            )}

            {ventana.tipo === 'contrasena' && (
                <DialogoContrasena
                    usuario={ventana.usuario}
                    alGuardar={refrescar}
                    alCerrar={() => setVentana({ tipo: 'ninguno' })}
                />
            )}
        </div>
    )
}

/* -------------------------------------------------------------------------- */

interface PropsUsuario {
    usuario: UsuarioAdmin | null
    roles: Rol[]
    cargandoRoles: boolean
    errorRoles: string | null
    alReintentarRoles: () => void
    alGuardar: () => void
    alCerrar: () => void
}

function DialogoUsuario({
    usuario,
    roles,
    cargandoRoles,
    errorRoles,
    alReintentarRoles,
    alGuardar,
    alCerrar,
}: PropsUsuario) {
    const [nombreUsuario, setNombreUsuario] = useState(usuario?.nombreUsuario ?? '')
    const [nombreCompleto, setNombreCompleto] = useState(usuario?.nombreCompleto ?? '')
    const [correo, setCorreo] = useState(usuario?.correo ?? '')
    const [contrasena, setContrasena] = useState('')
    const [elegidos, setElegidos] = useState<string[]>(usuario?.roles ?? [])
    const [activo, setActivo] = useState(usuario?.activo ?? true)
    const [sucursalId, setSucursalId] = useState<number | null>(usuario?.sucursalId ?? null)
    const [sucursales, setSucursales] = useState<Sucursal[]>([])
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    // Con una sola sucursal el campo sobra y solo estorba: se asigna sola y no
    // se muestra. El selector aparece recien cuando hay donde elegir.
    useEffect(() => {
        let vigente = true

        apiSucursales
            .listar(true)
            .then((lista) => {
                if (!vigente) {
                    return
                }

                setSucursales(lista)

                // Solo para usuarios nuevos: reasignar en silencio a uno que ya
                // existe le cerraria la sesion sin que nadie lo haya pedido.
                const unica = lista.length === 1 ? lista[0] : undefined

                if (unica && !usuario) {
                    setSucursalId(unica.sucursalId)
                }
            })
            .catch(() => {
                // Que no se pueda leer la lista no debe impedir editar el
                // nombre o los roles; el backend valida igual al guardar.
            })

        return () => {
            vigente = false
        }
    }, [usuario])

    function alternar(codigo: string) {
        setElegidos((actuales) =>
            actuales.includes(codigo)
                ? actuales.filter((c) => c !== codigo)
                : [...actuales, codigo],
        )
    }

    async function guardar(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        if (nombreCompleto.trim().length === 0) {
            setError('El nombre completo es obligatorio.')
            return
        }

        if (elegidos.length === 0) {
            setError('Debe asignarse al menos un rol.')
            return
        }

        if (!usuario && contrasena.length < 8) {
            setError('La contraseña debe tener al menos 8 caracteres.')
            return
        }

        if (sucursales.length > 1 && sucursalId === null) {
            setError('Elija la sucursal donde opera: con varias abiertas el sistema no la adivina.')
            return
        }

        setEnviando(true)

        try {
            if (usuario) {
                await apiUsuarios.actualizar(usuario.usuarioId, {
                    nombreCompleto: nombreCompleto.trim(),
                    correo: correo.trim() || undefined,
                    activo,
                    sucursalId,
                })

                // Los roles van en su propia llamada: el backend los trata como
                // una operacion distinta y la audita aparte.
                const cambiaron =
                    elegidos.length !== usuario.roles.length ||
                    elegidos.some((r) => !usuario.roles.includes(r))

                if (cambiaron) {
                    await apiUsuarios.asignarRoles(usuario.usuarioId, elegidos)
                }
            } else {
                await apiUsuarios.crear({
                    nombreUsuario: nombreUsuario.trim(),
                    nombreCompleto: nombreCompleto.trim(),
                    correo: correo.trim() || undefined,
                    contrasena,
                    roles: elegidos,
                    sucursalId,
                })
            }

            alGuardar()
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <Dialogo
            titulo={usuario ? 'Editar usuario' : 'Nuevo usuario'}
            ancho="ancho"
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="forma-usuario"
                        className="boton boton-primario"
                        disabled={enviando}
                    >
                        {enviando ? 'Guardando…' : 'Guardar'}
                    </button>
                </>
            }
        >
            <form id="forma-usuario" className="columna g4" onSubmit={guardar}>
                {!usuario && (
                    <div>
                        <label className="etiqueta" htmlFor="nombre-usuario">
                            Nombre de usuario
                        </label>
                        <input
                            id="nombre-usuario"
                            className="campo"
                            maxLength={60}
                            autoComplete="off"
                            value={nombreUsuario}
                            onChange={(e) => setNombreUsuario(e.target.value)}
                        />
                        <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                            Con el que entra al sistema. No se puede cambiar después.
                        </p>
                    </div>
                )}

                <div>
                    <label className="etiqueta" htmlFor="nombre-completo">
                        Nombre completo
                    </label>
                    <input
                        id="nombre-completo"
                        className="campo"
                        maxLength={160}
                        value={nombreCompleto}
                        onChange={(e) => setNombreCompleto(e.target.value)}
                    />
                </div>

                {sucursales.length > 1 && (
                    <div>
                        <label className="etiqueta" htmlFor="sucursal-usuario">
                            Sucursal
                        </label>
                        <select
                            id="sucursal-usuario"
                            className="campo"
                            value={sucursalId ?? ''}
                            onChange={(e) =>
                                setSucursalId(e.target.value === '' ? null : Number(e.target.value))
                            }
                        >
                            <option value="">Sin asignar</option>
                            {sucursales.map((s) => (
                                <option key={s.sucursalId} value={s.sucursalId}>
                                    {s.nombre}
                                </option>
                            ))}
                        </select>
                        <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                            Donde opera. Sin asignar no va a poder abrir caja ni vender.
                            {usuario && ' Cambiarla cierra su sesión.'}
                        </p>
                    </div>
                )}

                <div>
                    <label className="etiqueta" htmlFor="correo">
                        Correo <span className="tenue">(opcional)</span>
                    </label>
                    <input
                        id="correo"
                        className="campo"
                        type="email"
                        maxLength={160}
                        value={correo}
                        onChange={(e) => setCorreo(e.target.value)}
                    />
                </div>

                {!usuario && (
                    <div>
                        <label className="etiqueta" htmlFor="contrasena-inicial">
                            Contraseña inicial
                        </label>
                        <input
                            id="contrasena-inicial"
                            className="campo"
                            type="password"
                            minLength={8}
                            maxLength={128}
                            autoComplete="new-password"
                            value={contrasena}
                            onChange={(e) => setContrasena(e.target.value)}
                        />
                    </div>
                )}

                <div>
                    <span className="etiqueta">Roles</span>

                    <Estado
                        cargando={cargandoRoles}
                        error={errorRoles}
                        alReintentar={alReintentarRoles}
                        vacio={roles.length === 0}
                        mensajeVacio="No hay roles configurados."
                    >
                        <ul className="lista-simple">
                            {roles
                                .filter((r) => r.activo || elegidos.includes(r.codigo))
                                .map((r) => (
                                    <li key={r.rolId}>
                                        <label className="fila g2 interruptor">
                                            <input
                                                type="checkbox"
                                                checked={elegidos.includes(r.codigo)}
                                                onChange={() => alternar(r.codigo)}
                                            />
                                            <span className="columna">
                                                <span>{r.nombre}</span>
                                                <span className="tenue diminuto">
                                                    {r.descripcion ??
                                                        `${r.permisos.length} permisos`}
                                                </span>
                                            </span>
                                        </label>
                                    </li>
                                ))}
                        </ul>
                    </Estado>
                </div>

                {usuario && (
                    <>
                        <label className="fila g2 interruptor">
                            <input
                                type="checkbox"
                                checked={activo}
                                onChange={(e) => setActivo(e.target.checked)}
                            />
                            <span>Activo</span>
                        </label>
                        <p className="tenue diminuto">
                            Un usuario inactivo no puede entrar, pero su historial se conserva.
                        </p>
                    </>
                )}

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </form>
        </Dialogo>
    )
}

/* -------------------------------------------------------------------------- */

function DialogoContrasena({
    usuario,
    alGuardar,
    alCerrar,
}: {
    usuario: UsuarioAdmin
    alGuardar: () => void
    alCerrar: () => void
}) {
    const [contrasena, setContrasena] = useState('')
    const [repetida, setRepetida] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    async function guardar(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        if (contrasena.length < 8) {
            setError('La contraseña debe tener al menos 8 caracteres.')
            return
        }

        if (contrasena !== repetida) {
            setError('Las dos contraseñas no coinciden.')
            return
        }

        setEnviando(true)

        try {
            await apiUsuarios.restablecerContrasena(usuario.usuarioId, contrasena)
            alGuardar()
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <Dialogo
            titulo={`Contraseña de ${usuario.nombreUsuario}`}
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="forma-contrasena"
                        className="boton boton-primario"
                        disabled={enviando}
                    >
                        {enviando ? 'Guardando…' : 'Restablecer'}
                    </button>
                </>
            }
        >
            <form id="forma-contrasena" className="columna g4" onSubmit={guardar}>
                <div className="aviso aviso-atencion">
                    Se cierran todas las sesiones abiertas de este usuario. Entrégueselas en
                    persona y pídale que la cambie.
                </div>

                <div>
                    <label className="etiqueta" htmlFor="contrasena-nueva">
                        Contraseña nueva
                    </label>
                    <input
                        id="contrasena-nueva"
                        className="campo"
                        type="password"
                        minLength={8}
                        maxLength={128}
                        autoComplete="new-password"
                        value={contrasena}
                        onChange={(e) => setContrasena(e.target.value)}
                    />
                </div>

                <div>
                    <label className="etiqueta" htmlFor="contrasena-repetida">
                        Repetir contraseña
                    </label>
                    <input
                        id="contrasena-repetida"
                        className="campo"
                        type="password"
                        minLength={8}
                        maxLength={128}
                        autoComplete="new-password"
                        value={repetida}
                        onChange={(e) => setRepetida(e.target.value)}
                    />
                </div>

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </form>
        </Dialogo>
    )
}
